// slicer — list of selectable chips that drive the cross-filter store.
// AGENT-L: chip restyle lives in SlicerVisual.css (12px font, 6×12 padding,
// 28px min-height, accent-soft hover, accent fill + inset ring on selected).
// The slicer's title comes from ChartShell — the muted-semibold "header"
// treatment from the design spec is applied via _chartShell title styling
// owned by Agent C, with .slicer-header available here for explicit groups.

import { useMemo } from "react";
import { ChartShell } from "./_chartShell";
import type { VisualProps } from "./types";
import { useFilterStore } from "../state/filterStore";
import { firstFieldRef } from "./_echartsHelpers";
import "./SlicerVisual.css";

export function SlicerVisual({ spec, data }: VisualProps) {
  const toggleFilter = useFilterStore((s) => s.toggleFilter);
  const filters = useFilterStore((s) => s.filters);

  const fields = spec.fields ?? {};
  const field = firstFieldRef(fields.Values) ?? firstFieldRef(fields.Category);

  const options = useMemo(() => {
    if (data.options && data.options.length > 0) return data.options;
    if (data.rows && field?.column) {
      const seen = new Set<string>();
      const list: Array<{ value: string; label?: string }> = [];
      for (const r of data.rows) {
        const raw = r[field.column];
        if (raw === null || raw === undefined) continue;
        const v = String(raw);
        if (!seen.has(v)) {
          seen.add(v);
          list.push({ value: v });
        }
      }
      return list;
    }
    return [];
  }, [data.options, data.rows, field?.column]);

  const filterKey = field?.table && field?.column ? `${field.table}.${field.column}` : null;
  const current = filterKey ? filters[filterKey] : undefined;

  return (
    <ChartShell title={spec.title} isEmpty={options.length === 0} status={data.status}>
      <div className="slicer-list">
        {options.map((o) => {
          const v = o.value;
          const selected =
            current !== undefined &&
            current.op === "eq" &&
            JSON.stringify(current.value) === JSON.stringify(v);
          return (
            <button
              key={String(v)}
              type="button"
              className={selected ? "slicer-chip is-selected" : "slicer-chip"}
              onClick={() => {
                if (!filterKey) return;
                toggleFilter(filterKey, { op: "eq", value: v as string });
              }}
            >
              {o.label ?? String(v)}
            </button>
          );
        })}
      </div>
    </ChartShell>
  );
}
